// Nazwa pliku: Helpers.js
// Autor: Centrum Informatyki Zeto SA
import {
  InputTypeConstants,
  MiscConstants,
  SortDirectionConstants,
} from "./constants/index";
import { Buffer } from "buffer";
import { format } from "date-fns";
import { toast } from "react-toastify";

/**
 * Tworzenie opcji do kontrolki wyboru
 * @param {string} name Nazwa
 * @param {string} value Wartość
 * @param {string} className Klasy CSS opcji
 * @returns Obiekt do kontrolki wyboru
 */
export function createOption(name, value, className = "") {
  return { name, value, className };
}

/**
 * Tworzenie obiektu ze schematem tworzenia nagłówka tabeli
 * @param {string} displayName Nazwa wyświetlana
 * @param {string} columnName Nazwa kolumny
 * @param {string} ariaLabel Nazwa kolumny - aria label
 * @param {boolean} isSortingEnabled Czy sortowanie po kolumnie jest dostępne
 * @param {string} cssClassHeader Klasy css nagłówka
 * @param {function} customRender Funkcja renderująca zawartość wiersza w danej kolumnie
 * @param {string} cssClassBody Klasy css treści
 * @param {string} dateFormat Format daty (np. "dd.MM.yyyy HH:mm:ss")
 * @returns Obiekt schematu nagłówka tabeli
 */
export function getTableHeader(
  displayName = null,
  columnName = null,
  ariaLabel = null,
  isSortingEnabled = false,
  cssClassHeader = "",
  customRender = null,
  cssClassBody = "",
  dateFormat = "",
  onBodyClick = null
) {
  return {
    displayName,
    columnName,
    ariaLabel,
    isSortingEnabled,
    cssClassHeader,
    cssClassBody,
    customRender,
    dateFormat,
    onBodyClick,
  };
}

/**
 * Tworzenie obiektu ze schematem tworzenia kolumny tabeli raportu
 * @param {string} displayText Wyświetlany teskt kolumny
 * @param {string} name Nazwa kolumny
 * @param {boolean} visible Czy kolumna ma być widoczna
 * @param {boolean} isDate Czy jest to data
 */
export function getReportTableColumn(
  displayText = null,
  name = null,
  visible = true,
  sortDirection = SortDirectionConstants.ASC,
  sortOrder,
  isDate = false
) {
  return {
    displayText,
    name,
    visible,
    sortDirection,
    sortOrder,
    isDate,
  };
}

/** Sortowanie kolumn raportu po właściowści sortOrder
 * @param {object} columnA Kolumna pierwsza
 * @param {object} columnB Kolumna druga
 * @returns Czy kolumna powinna być posortowana niżej lub wyżej
 */
export function sortColumnsBySortOrder(columnA, columnB) {
  return columnA.sortOrder - columnB.sortOrder;
}

/**
 * Tworzenie obiektu schematu pola wyszukiwarki
 * @param {SearchParamTypeConstants} fieldSearchType Typ wyszukiwania
 * @param {string} columnName Nazwa kolumny
 * @param {string} label Etykieta
 * @param {InputTypeConstants} type Typ pola
 * @param {Array} options Tablica opcji do kontrolki wyboru
 * @returns Obiekt schematu pola wyszukiwarki
 */
export function getSearchField(
  fieldSearchType = null,
  columnName = null,
  label = null,
  type = null,
  options = [],
  defaultValue = "",
  hidden = false,
  colSpan = 6
) {
  return {
    fieldSearchType,
    columnName,
    name: `${fieldSearchType}${capitalize(columnName)}`,
    label,
    type,
    options,
    defaultValue,
    hidden,
    colSpan,
  };
}

/**
 * Ustawienie domyślnych parametrów wyszukiwania
 * @param {Array} searchFields
 * @returns Parametry wyszukiwania
 */
export function getDefaultSearchParams(searchFields = []) {
  let result = {};
  searchFields.forEach((value) => {
    switch (value.type) {
      case InputTypeConstants.checkbox:
        result[value.name] = false.toString();
        break;
      case InputTypeConstants.string:
        result[value.name] = value.defaultValue;
        break;
      case InputTypeConstants.date:
      default:
        result[value.name] = "";
        break;
    }
  });
  return result;
}
/**
 * Wyselekcjonowanie numeru z tekstu
 * @param {string} number Numer do ograniczenia przez Regex
 * @returns Ograniczony przez regex numer
 */
export function splitPhoneFromText(number) {
  const phoneRegex = /\d{11}|\d{9}/g;
  const countryRegex = /[+]\d{11}/g;
  const phoneNumber = number?.match(countryRegex)
    ? number?.match(countryRegex).pop()
    : number?.match(phoneRegex)?.pop();

  return phoneNumber ? phoneNumber : "";
}

/**
 * Zamiana pierwszej litery tekstu na wielką
 * @param {string} string Teksto do zmiany
 * @returns Tekst po zmianie pierwszej litery na wielką
 */
export function capitalize(text) {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/**
 * Zamienia tablicę obiektów na obiekt, którego kluczami są wartości właściwości key, a wartościami wartości właściwości value
 * @param {Array} array Tablica obiektów
 * @param {string} key Nazwa właściwości, której wartości będą kluczami obiektu
 * @param {string} value Nazwa właściwości, której wartości będą wartościami obiektu
 * @param {boolean} enableDefaultBoolValue Czy ustawiać domyślną wartość bool
 * @param {boolean} defaultBoolValue Domyślna wartość parametrów typu bool
 * @example
 * // returns { John: 11, Mary: 18 }
 * convertArrayToObject([ { name: "John", age: 11 }, { name: "Mary", age: 18 } ], name, age);
 * @returns {object} Zwraca obiekt o odpowiedniej strukturze
 */
export function convertArrayToObject(
  array,
  key,
  value,
  enableDefaultBoolValue = false,
  defaultBoolValue = false
) {
  const initialValue = {};
  return array.reduce((obj, item) => {
    return {
      ...obj,
      [item[key]]:
        item.type === 5 && enableDefaultBoolValue
          ? defaultBoolValue
          : item[value],
    };
  }, initialValue);
}

/**
 * Sprawdzenie czy podana tablica zawiera wszystkie elementy drugiej tablicy
 * @param {Array} array Sprawdzana tablica
 * @param {Array} requiredElements Wymagane elementy w sprawdzanej tablicy
 * @returns {boolean} Czy podana tablica zawiera wszystkie elementy drugiej tablicy
 */
export function containsAll(array, requiredElements) {
  return requiredElements.every((requiredElement) =>
    array.includes(requiredElement)
  );
}

/**
 * Sprawdzenie czy podana tablica zawiera przynajmniej jeden element drugiej tablicy
 * @param {*} array Sprawdzana tablica
 * @param {*} searchElements Poszukiwane elementy w sprawdzanej tablicy
 * @returns {boolean} Czy podana tablica zawiera przynajmniej jeden element drugiej tablicy
 */
export function containsAny(array, searchElements) {
  return searchElements.some((searchElement) => array.includes(searchElement));
}

/**
 * Tworzenie obiektu użytkownika z domyślnymi wartościami (użytkownik niezalogowany)
 * @returns Obiekt użytkownika
 */
export function getDefaultUser() {
  return {
    id: "",
    firstName: "",
    lastName: "",
    permissions: [],
    roles: [],
    currentStep: null,
  };
}

export function updateUserCurrentStep(user, updatedCurrentStep) {
  const copyOfUser = { ...user };
  copyOfUser.currentStep = updatedCurrentStep;
  return copyOfUser;
}

/**
 * Konwersja pliku na base64
 * @param {file} file Plik
 * @returns Plik zakodowany jako base64
 */
export function convertFileToBase64(file) {
  return new Promise((resolve, reject) => {
    const fileReader = new FileReader();
    fileReader.readAsDataURL(file);
    fileReader.onload = () => {
      resolve(fileReader.result);
    };
    fileReader.onerror = (error) => {
      reject(error);
    };
  });
}

/**
 * Konwersja base64 do pliku
 * @param {string} base64 Plik zakodowany jako base64
 * @returns Plik
 */
export function convertBase64ToFile(base64) {
  return new File([new Blob([base64])], "file");
}

/**
 * Sprawdzenie, czy uprawnienie znajduje się w liście uprawnień użytkownika
 * @param {Array} permissionList Lista uprawnień
 * @param {string} permission Sprawdzane uprawnienie
 * @returns Czy uprawnienie znajduje się na liście
 */
export function checkIfPermissionListContainsPermission(
  permissionList,
  permission
) {
  return permissionList.includes(permission);
}

/**
 * Pobranie zagnieżdżonej właściwości obiektu
 * @param {object} object Obiekt
 * @param {string} string Nazwa właściwości
 * @returns Wartość właściwości
 */
export function getObjectNestedProperty(object, string) {
  return string
    ?.split(".")
    .reduce((prev, current) => (prev && prev[current]) || null, object);
}

/**
 * Zamienia plik na tablicę bajtów w standardzie base64 jako string.
 * @param {FileList[0]} file Plik
 * @var {FileReader} reader Zczytuje plik
 * @var {Uint8Array} fileByteArray Tablica bajtów Uint8
 * @var {string} b64 Przekonwertowana tablica Uint8
 * @example
 * // returns: "UEsDBBQABgAIAAAAIQCxwT"
 * @returns {string} Zwraca string o odpowiedniej wartości
 */
export function fileToByteArray(file) {
  return new Promise((resolve, reject) => {
    try {
      let reader = new FileReader();
      let fileByteArray = [];
      //let file = e.target.files[0];
      reader.readAsArrayBuffer(file);
      reader.onloadend = (evt) => {
        if (evt.target.readyState === FileReader.DONE) {
          let arrayBuffer = evt.target.result,
            array = new Uint8Array(arrayBuffer);
          for (let byte of array) {
            fileByteArray.push(byte);
          }
        }
        var b64 = Buffer.from(fileByteArray).toString("base64");
        resolve(b64);
      };
    } catch (e) {
      reject(e);
    }
  });
}

/**
 * Pobranie pliku z odpowiedzi na żądanie
 * @param {object} response Odpowiedź na żądanie
 * @param {string} filename Nazwa pliku
 */
export function downloadFileFromResponse(response, filename = null) {
  const href = URL.createObjectURL(response.data);
  const link = document.createElement("a");
  link.href = href;
  link.download = filename ?? extractFilenameFromResponseHeader(response);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(href);
}

/**
 * Wydobycie nazwy pliku z nagłówka odpowiedzi na żądanie
 * @param {object} response Odpowiedź na żądanie
 * @returns Nazwa pliku
 */
export function extractFilenameFromResponseHeader(response) {
  const disposition = response.headers["content-disposition"];
  let filename = disposition.split(/;(.+)/)[1].split(/=(.+)/)[1];
  if (filename.toLowerCase().startsWith("utf-8''"))
    return decodeURIComponent(filename.replace(/utf-8''/gi, ""));
  else return filename.replace(/['"]/g, "");
}

/**
 * Pobranie opcji wyboru płci
 * @returns Opcje wyboru płci
 */
export function getGenderOptions() {
  return [
    createOption("(wybierz)", null),
    createOption("kobieta", "kobieta"),
    createOption("mężczyzna", "mężczyzna"),
  ];
}

/**
 * Utworzenie opcji wyboru na podstawie obiektu enumeratora
 * @param {object} enumerator Enumerator z wartościami i opisami
 * @param {boolean} descriptionAsValue Czy opisy enumeratora mają być również wartościami opcji wyboru
 * @returns Lista opcji wygenerowana na podstawie podanego w parametrze enumeratora
 */
export function createOptionsFromEnum(enumerator, descriptionAsValue = false) {
  const options = [createOption("(wybierz)", null)];
  Object.keys(enumerator).forEach(function (key, index) {
    const description = enumerator[key].description;
    const value = descriptionAsValue ? description : enumerator[key].value;
    options.push(createOption(description, value));
  });
  return options;
}

/**
 * Zwrócenie opisu opcji enumeratora dla danej wartości
 * @param {object} enumerator Enumerator z wartościami i opisami
 * @param {number} value Wartość enumeratora
 * @returns Opis danej wartości
 */
export function getEnumDescriptionByValue(enumerator, value) {
  const keys = Object.keys(enumerator);
  for (const key of keys) {
    if (enumerator[key].value === value) {
      return enumerator[key].description;
    }
  }
  return null; // Return null if the value is not found
}

/**
 * Metoda zwracająca łańcuch tekstowy zawierający liczbę z wiodącymi zerami
 * @param {Number} num Liczba
 * @param {Number} len Długość wynikowego łańcucha
 * @returns Łańcuch tekstowy
 */
export function pad(num, len) {
  const resultLength =
    len > num.toString().length ? len : num.toString().length;
  return (Array(resultLength).join("0") + num).slice(-resultLength);
}

/**
 * Standardowa obsługa błędów wykonania żądania
 * Pokazuje użytkownikowi toast z komunikatem błędu
 * @param {object} error Obiekt błędu
 */
export function handleRequestError(error) {
  toast.error(error.response?.data?.detail ?? error.message);
}

/**
 * Standardowa obsługa pomyślnego powiadomienia wykonania żądania
 * Pokazuje użytkownikowi toast z pomyślnym komunikatem
 */
export function toastSuccessMessage(message) {
  toast.success(message, {
    autoClose: 1700,
  });
}

/**
 * Standardowa obsługa ostrzeżenia wykonania żądania
 * Pokazuje użytkownikowi toast z ostrzeżegającym komunikatem
 */
export function toastWarningMessage(message) {
  toast.warning(message, {
    autoClose: 1700,
  });
}

/**
 * Zwraca ciąg znaków przedstawiający czas w formacie hh:mm:ss
 * @param {Number} time Liczba milisekund
 * @returns Ciąg znaków
 */
export function formatMilisecondsToHhMmSs(time) {
  const hours = ~~(time / 3_600_000);
  const minutes = ~~((time % 3_600_000) / 60 / 1000);
  const seconds = ~~((time % 60_000) / 1000);
  return `${pad(hours, 2)}:${pad(minutes, 2)}:${pad(seconds, 2)}`;
}

/**
 * Zwraca ciąg znaków przedstawiający czas w formacie mm:ss
 * @param {Number} time Liczba milisekund
 * @returns Ciąg znaków
 */
export function formatMilisecondsToMmSs(time) {
  const minutes = ~~(time / 60 / 1000);
  const seconds = ~~((time % 60_000) / 1000);
  return `${pad(minutes, 2)}:${pad(seconds, 2)}`;
}

/**
 * Konwertuje ciąg czasu w formacie "HH:MM:SS" na liczbę sekund.
 *
 * @param {string} duration - Ciąg czasu w formacie "HH:MM:SS".
 * @returns {number|undefined} - Liczba sekund lub undefined, jeśli podany format czasu jest nieprawidłowy.
 */

export function convertHhMmSsStringToSeconds(duration) {
  const durationParts = duration.join("").split(":");
  if (durationParts.length === 3)
    return (
      Number(durationParts[0]) * 60 * 60 +
      Number(durationParts[1]) * 60 +
      Number(durationParts[2])
    );
}

/**
 * Konwertuje podany ciąg znaków na ciąg znaków w formacie tytułu
 * @param {string} string Ciąg znaków do konwersji
 * @returns Ciąg znaków w formacie tytułu
 */
export function toTitleCase(string) {
  const stringWithSpaces = string.replace(/([A-ZĄĘĆÓŻŹ])/g, " $1");
  return stringWithSpaces.charAt(1) + stringWithSpaces.slice(2).toLowerCase();
}

/**
 * Metoda sprowadzająca tablicę obiektów typu T z zagnieżdżonymi kolejnymi tablicami obiektów typu T (itd.)
 * do jednego poziomu zagnieżdżenia
 * @param {Array} arrayOfObjects Tablica obiektów
 * @param {string} propertyName Nazwa właściwości zawierającej tablicę obiektów
 */
export function flattenArrayOfObjects(arrayOfObjects, propertyName) {
  return arrayOfObjects.reduce((acc, current) => {
    acc = acc.concat(current);
    if (current[propertyName]) {
      acc = acc.concat(
        flattenArrayOfObjects(current[propertyName], propertyName)
      );
      delete current[propertyName];
    }
    return acc;
  }, []);
}

/**
 * Funkcja służy do wygenerowania skrótu (hasza) dla podanego łańcucha znaków.
 * Implementacja wykorzystuje prosty algorytm oparty na operatorze XOR.
 *
 * @param {string} string - Łańcuch znaków, dla którego wygenerować hasz
 * @returns {string} - Wygenerowany hasz w postaci łańcucha szesnastkowego
 */
export function hashString(string) {
  let hash = 0;
  if (string.length === 0) return hash;

  for (let i = 0; i < string.length; i++) {
    const char = string.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash;
  }
  return hash;
}

/**
 * Zwraca znormalizowaną liczbę w zakresie [min, max)
 * @param {number} number - Liczba do znormalizowania
 * @param {number} min - Dolna granica zakresu
 * @param {number} max - Górna granica zakresu (wyłącznie)
 * @returns {number} - Znormalizowana liczba w zakresie [min, max)
 */
export function normalizeNumber(number, min, max) {
  const range = max - min;
  const adjusted = (number - min) % range;
  const result = adjusted < 0 ? adjusted + range : adjusted;
  return result + min;
}

/**
 * Porównuje rekurencyjnie wartości właściwości dwóch obiektów
 * @param {Object} object1 - Pierwszy obiekt do porównania
 * @param {Object} object2 - Drugi obiekt do porównania
 * @returns {boolean} - Zwraca true, jeśli obiekty mają identyczną zawartość, w przeciwnym razie false.
 */
export function deepEqual(object1, object2) {
  const keys1 = Object.keys(object1);
  const keys2 = Object.keys(object2);

  if (keys1.length !== keys2.length) {
    return false;
  }

  for (const key of keys1) {
    const val1 = object1[key];
    const val2 = object2[key];
    const areObjects = isObject(val1) && isObject(val2);
    if (
      (areObjects && !deepEqual(val1, val2)) ||
      (!areObjects && val1 !== val2)
    ) {
      return false;
    }
  }

  return true;
}

/**
 * Sprawdza czy podana wartość jest obiektem
 * @param {any} object - Wartość do sprawdzenia
 * @returns {boolean} - Zwraca true, jeśli wartość jest obiektem, w przeciwnym razie false
 */
function isObject(object) {
  return object != null && typeof object === "object";
}

/**
 * Generuje ciąg znaków w formacie HSL na podstawie podanych danych.
 *
 * @param {string} data - Dane wejściowe do wygenerowania ciągu HSL.
 * @returns {string} - Wygenerowany ciąg znaków w formacie HSL.
 */
export function generateHslString(data) {
  const hash = hashString(data);
  const h = normalizeNumber(hash, 0, 360);
  const s = normalizeNumber(hash, 50, 75);
  const l = normalizeNumber(hash, 25, 60);
  const hslArray = [h, s, l];

  return `hsl(${hslArray[0]}, ${hslArray[1]}%, ${hslArray[2]}%)`;
}

/**
 * Sprawdza czy daty są równe (dzień, miesiąc, rok)
 * @param {Date} date1 Pierwsza data do porównania
 * @param {Date} date2 Druga data do porównania
 * @returns Prawda, jeśli wartości są takie same. Fałsz, jeśli daty są różne.
 */
export function equalDates(date1, date2) {
  return (
    format(new Date(date1), MiscConstants.dateFormat) ===
    format(new Date(date2), MiscConstants.dateFormat)
  );
}

/**
 * Sprawdza czy podana wartość jest pustym obiektem bez żadnych właściwości
 * @param {any} obj Wartość do sprawdzenia
 * @returns Prawda, jeśli podano pusty obiekt
 */
export function isObjectEmpty(obj) {
  return Object.keys(obj).length === 0 && obj.constructor === Object;
}

/**
 * Wylicza pozostały czas
 * @param {Date} firstEnterData Data od której sprawdza
 * @param {number} daysToComplete Liczba dni do której sprawdza
 * @returns Zwraca null gdy czas uplynal, liczbe dni lub godzin gdy czas jeszcze pozostał
 */
export function calculateRemainingTime(firstEnterData, daysToComplete){
  if (!firstEnterData || !daysToComplete) return null;

  const createdAt = new Date(firstEnterData);
  const now = new Date();
  const diffMs = now - createdAt;
  const diffHours = Math.ceil(diffMs / (1000 * 60 * 60));

  const totalHours = parseInt(daysToComplete) * 24;
  const hoursRemaining = totalHours - diffHours;

  if (hoursRemaining > 0) {
    const daysRemaining = Math.floor(hoursRemaining / 24);
    const extraHours = hoursRemaining % 24;

    if (daysRemaining >= 1) {
      const roundedDays = extraHours > 0 ? daysRemaining + 1 : daysRemaining;
      return `${roundedDays} dni`;
    } else {
      return formatHours(hoursRemaining);
    }
  } else {
    return null;
  }
};

/**
 * Formatuje liczbe godzin na odpowiednią etkietę 1 godzina, 2 godziny etc.
 */
function formatHours(hoursRemaining) {
  if (hoursRemaining === 1) {
    return `${hoursRemaining} godzina`;
  } else if (
    [2, 3, 4].includes(hoursRemaining % 10) && 
    ![12, 13, 14].includes(hoursRemaining % 100)
  ) {
    return `${hoursRemaining} godziny`;
  } else {
    return `${hoursRemaining} godzin`;
  }
}